import { Component, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";

interface State {
  error: Error | null;
}

class ErrorBoundaryInner extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: unknown) {
    console.error("ErrorBoundary caught:", error, info);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-subtle">
            <AlertCircle className="h-5 w-5 text-danger" />
          </div>
          <h2 className="text-lg font-medium">Something went wrong.</h2>
          <p className="mt-1 text-sm text-text-muted">
            The page hit an unexpected error. Refreshing usually fixes it.
          </p>
          <div className="mt-4">
            <Button
              onClick={() => window.location.reload()}
              variant="secondary"
              size="sm"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

// Reset the boundary on every navigation so a stale error from one page
// doesn't permanently break the next one. `key` remounts the class component.
export function ErrorBoundary({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return <ErrorBoundaryInner key={pathname}>{children}</ErrorBoundaryInner>;
}
